import { useRef, useState } from "react";
import { uploadAffiliatePhoto } from "@/lib/affiliateUpload";
import {
  disponibilidadOptions,
  documentosVehiculoOptions,
  tiposCargaAffiliateOptions,
  tiposVehiculoOptions,
} from "@/lib/affiliateOptions";
import { Field, inputClass } from "./ui";
import DocumentSlots from "./DocumentSlots";
import type { DocumentoForm, VehiculoForm } from "./types";

interface VehicleEditorProps {
  vehiculo: VehiculoForm;
  index: number;
  errors: Record<string, string>;
  canRemove: boolean;
  onChange: (patch: Partial<VehiculoForm>) => void;
  onRemove: () => void;
}

export default function VehicleEditor({
  vehiculo,
  index,
  errors,
  canRemove,
  onChange,
  onRemove,
}: VehicleEditorProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const [open, setOpen] = useState(true);

  const err = (field: string) => errors[`veh-${index}-${field}`];

  const handlePhoto = async (file: File) => {
    setPhotoError("");
    setUploading(true);
    try {
      const url = await uploadAffiliatePhoto(file);
      onChange({ fotoUrl: url });
    } catch (uploadError) {
      setPhotoError(uploadError instanceof Error ? uploadError.message : "No pudimos subir la foto.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const setDocs = (docs: DocumentoForm[]) => onChange({ documentos: docs });

  return (
    <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
      <div className="flex items-center justify-between gap-3 border-b border-background-200 bg-background-100 px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 text-left"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-500 text-sm font-label font-semibold text-background-50">
            {index + 1}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-heading font-semibold text-foreground-950">
              {vehiculo.marca || vehiculo.tipo
                ? `${vehiculo.marca} ${vehiculo.modelo}`.trim() || vehiculo.tipo
                : "Nuevo vehículo"}
            </span>
            <span className="block truncate text-xs text-foreground-500">
              {vehiculo.placa ? `Placa ${vehiculo.placa}` : "Completa los datos del vehículo"}
            </span>
          </span>
          <i
            className={`${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} ml-auto text-lg leading-none text-foreground-500`}
            aria-hidden="true"
          ></i>
        </button>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label="Eliminar vehículo"
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-500 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-delete-bin-6-line text-base leading-none" aria-hidden="true"></i>
          </button>
        )}
      </div>

      {open && (
        <div className="p-4 md:p-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Tipo de vehículo" htmlFor={`tipo-${vehiculo.id}`} required error={err("tipo")}>
              <select
                id={`tipo-${vehiculo.id}`}
                value={vehiculo.tipo}
                onChange={(event) => onChange({ tipo: event.target.value })}
                className={inputClass}
              >
                <option value="">Selecciona un tipo</option>
                {tiposVehiculoOptions.map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {tipo}
                  </option>
                ))}
              </select>
            </Field>
            {vehiculo.tipo === "Otro" && (
              <Field label="Especifica el tipo" htmlFor={`tipoOtro-${vehiculo.id}`} required error={err("tipoPersonalizado")}>
                <input
                  id={`tipoOtro-${vehiculo.id}`}
                  type="text"
                  value={vehiculo.tipoPersonalizado}
                  onChange={(event) => onChange({ tipoPersonalizado: event.target.value })}
                  placeholder="Ej. Camión grúa"
                  className={inputClass}
                />
              </Field>
            )}
            <Field label="Marca" htmlFor={`marca-${vehiculo.id}`} required error={err("marca")}>
              <input
                id={`marca-${vehiculo.id}`}
                type="text"
                value={vehiculo.marca}
                onChange={(event) => onChange({ marca: event.target.value })}
                placeholder="Ej. Isuzu"
                className={inputClass}
              />
            </Field>
            <Field label="Modelo" htmlFor={`modelo-${vehiculo.id}`} required error={err("modelo")}>
              <input
                id={`modelo-${vehiculo.id}`}
                type="text"
                value={vehiculo.modelo}
                onChange={(event) => onChange({ modelo: event.target.value })}
                placeholder="Ej. NPR"
                className={inputClass}
              />
            </Field>
            <Field label="Año" htmlFor={`anio-${vehiculo.id}`} optional>
              <input
                id={`anio-${vehiculo.id}`}
                type="text"
                value={vehiculo.anio}
                onChange={(event) => onChange({ anio: event.target.value })}
                placeholder="Ej. 2021"
                className={inputClass}
              />
            </Field>
            <Field label="Color" htmlFor={`color-${vehiculo.id}`} optional>
              <input
                id={`color-${vehiculo.id}`}
                type="text"
                value={vehiculo.color}
                onChange={(event) => onChange({ color: event.target.value })}
                placeholder="Ej. Blanco"
                className={inputClass}
              />
            </Field>
            <Field label="Placa" htmlFor={`placa-${vehiculo.id}`} required error={err("placa")}>
              <input
                id={`placa-${vehiculo.id}`}
                type="text"
                value={vehiculo.placa}
                onChange={(event) => onChange({ placa: event.target.value })}
                placeholder="Ej. A123456"
                className={inputClass}
              />
            </Field>
            <Field label="Capacidad" htmlFor={`capacidad-${vehiculo.id}`} optional hint="Ej. 5 toneladas, 12 m³, 40 pasajeros">
              <input
                id={`capacidad-${vehiculo.id}`}
                type="text"
                value={vehiculo.capacidad}
                onChange={(event) => onChange({ capacidad: event.target.value })}
                placeholder="Ej. 3.5 toneladas"
                className={inputClass}
              />
            </Field>
            <Field label="Tipo de carga" htmlFor={`tipoCarga-${vehiculo.id}`} optional>
              <select
                id={`tipoCarga-${vehiculo.id}`}
                value={vehiculo.tipoCarga}
                onChange={(event) => onChange({ tipoCarga: event.target.value })}
                className={inputClass}
              >
                <option value="">Selecciona el tipo de carga</option>
                {tiposCargaAffiliateOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Características"
              htmlFor={`caracteristicas-${vehiculo.id}`}
              optional
              full
              hint="Separadas por comas. Ej. Caja seca, rampa hidráulica, aire acondicionado"
            >
              <input
                id={`caracteristicas-${vehiculo.id}`}
                type="text"
                value={vehiculo.caracteristicas}
                onChange={(event) => onChange({ caracteristicas: event.target.value })}
                placeholder="Caja seca, rampa hidráulica"
                className={inputClass}
              />
            </Field>
          </div>

          <hr className="my-5 border-background-200" />

          <h4 className="mb-3 text-sm font-heading font-semibold text-foreground-950">
            Disponibilidad de este vehículo
          </h4>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Zona habitual de operación" htmlFor={`zona-${vehiculo.id}`} optional>
              <input
                id={`zona-${vehiculo.id}`}
                type="text"
                value={vehiculo.zonaOperacion}
                onChange={(event) => onChange({ zonaOperacion: event.target.value })}
                placeholder="Ej. Santo Domingo y alrededores"
                className={inputClass}
              />
            </Field>
            <Field label="Disponibilidad" htmlFor={`disp-${vehiculo.id}`} optional>
              <select
                id={`disp-${vehiculo.id}`}
                value={vehiculo.disponibilidad}
                onChange={(event) => onChange({ disponibilidad: event.target.value })}
                className={inputClass}
              >
                {disponibilidadOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Días disponibles" htmlFor={`dias-${vehiculo.id}`} optional>
              <input
                id={`dias-${vehiculo.id}`}
                type="text"
                value={vehiculo.diasDisponibles}
                onChange={(event) => onChange({ diasDisponibles: event.target.value })}
                placeholder="Ej. Lunes a sábado"
                className={inputClass}
              />
            </Field>
            <Field label="Horario" htmlFor={`horario-${vehiculo.id}`} optional>
              <input
                id={`horario-${vehiculo.id}`}
                type="text"
                value={vehiculo.horario}
                onChange={(event) => onChange({ horario: event.target.value })}
                placeholder="Ej. 7:00 AM – 6:00 PM"
                className={inputClass}
              />
            </Field>
            <Field label="Distancia máxima" htmlFor={`distancia-${vehiculo.id}`} optional>
              <input
                id={`distancia-${vehiculo.id}`}
                type="text"
                value={vehiculo.distanciaMaxima}
                onChange={(event) => onChange({ distanciaMaxima: event.target.value })}
                placeholder="Ej. 200 km"
                className={inputClass}
              />
            </Field>
          </div>

          <hr className="my-5 border-background-200" />

          <h4 className="mb-3 text-sm font-heading font-semibold text-foreground-950">
            Fotografía del vehículo
          </h4>
          <div className="flex items-start gap-4">
            <div className="flex h-24 w-32 shrink-0 items-center justify-center overflow-hidden rounded-md border border-background-300 bg-background-100">
              {vehiculo.fotoUrl ? (
                <img src={vehiculo.fotoUrl} alt="" className="h-full w-full object-cover object-top" />
              ) : (
                <i className="ri-image-line text-2xl leading-none text-foreground-400" aria-hidden="true"></i>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void handlePhoto(file);
                }}
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-3.5 py-2 text-sm font-label font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:opacity-50"
                >
                  <i
                    className={`${uploading ? "ri-loader-4-line animate-spin" : "ri-camera-line"} text-base leading-none`}
                    aria-hidden="true"
                  ></i>
                  {uploading ? "Optimizando…" : vehiculo.fotoUrl ? "Cambiar foto" : "Subir / tomar foto"}
                </button>
                {vehiculo.fotoUrl && (
                  <button
                    type="button"
                    onClick={() => onChange({ fotoUrl: "" })}
                    className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-3.5 py-2 text-sm font-label font-medium text-foreground-600 transition-colors hover:bg-background-100"
                  >
                    <i className="ri-delete-bin-6-line text-base leading-none" aria-hidden="true"></i>
                    Quitar
                  </button>
                )}
              </div>
              <p className="mt-2 text-xs text-foreground-500">
                Optimizamos la imagen automáticamente para que cargue rápido. Puedes tomarla con la
                cámara del teléfono.
              </p>
              {photoError && (
                <p className="mt-1.5 flex items-center gap-1 text-xs text-primary-700">
                  <i className="ri-error-warning-line text-sm leading-none" aria-hidden="true"></i>
                  {photoError}
                </p>
              )}
            </div>
          </div>

          <hr className="my-5 border-background-200" />

          <h4 className="mb-1 text-sm font-heading font-semibold text-foreground-950">
            Documentación del vehículo
          </h4>
          <p className="mb-3 text-xs text-foreground-500">
            Opcional por ahora. Los documentos se guardan de forma privada y solo el equipo de AXONIA
            GROUP puede verlos.
          </p>
          <DocumentSlots
            tipos={documentosVehiculoOptions}
            docs={vehiculo.documentos}
            onChange={setDocs}
            folder="vehiculos"
          />
        </div>
      )}
    </div>
  );
}